import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { useLocalePath } from "../app/localization";
import { useTranslation } from "react-i18next";
import TrustBar from "./TrustBar";
import { trackAnalyticsEvent } from "../analytics/analytics";
import { useId, useState, type FormEvent } from "react";

export default function Footer() {
  const path = useLocalePath();
  const { t } = useTranslation("footer");
  const newsletterEmailId = useId();
  const newsletterHintId = `${newsletterEmailId}-hint`;
  const newsletterErrorId = `${newsletterEmailId}-error`;
  const newsletterStatusId = `${newsletterEmailId}-status`;
  const [newsletterError, setNewsletterError] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("");

  function onNewsletterSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const emailInput = e.currentTarget.elements.namedItem(
      "newsletter-email"
    ) as HTMLInputElement | null;

    if (!emailInput?.validity.valid) {
      setNewsletterStatus("");
      setNewsletterError(t("newsletter.invalid_email"));
      emailInput?.focus();
      return;
    }

    setNewsletterError("");
    setNewsletterStatus(t("newsletter.success"));
    trackAnalyticsEvent("newsletter_signup", {
      location: "footer",
    });
    e.currentTarget.reset();
  }

  return (
    <footer className="border-t border-slate-200 bg-slate-950 pt-12 text-slate-300">
      <div className="mx-auto grid max-w-[1180px] grid-cols-1 gap-8 px-4 sm:grid-cols-2 sm:px-6 md:grid-cols-4 lg:px-8">
        {/* Column 1: Logo + description + social */}
        <div>
          <Link to={path("/")} className="flex items-center space-x-2">
            <picture>
              <source srcSet="/optimized/logo.avif" type="image/avif" />
              <source srcSet="/optimized/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="Nalosoft logo"
                width={380}
                height={75}
                decoding="async"
                loading="lazy"
                className="h-7 w-auto"
              />
            </picture>
          </Link>
          <p className="mt-4 text-sm leading-6 text-slate-400">
            {t("description")}
          </p>
          <nav
            aria-label={t("aria.socialLinks")}
            className="flex items-center space-x-4 mt-4"
          >
            <a
              href="https://www.facebook.com/nalosoft"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white"
              aria-label="Facebook"
            >
              <Icon icon="basil:facebook-solid" width={20} height={20} />
            </a>
            <a
              href="https://www.instagram.com/nalosoftcom"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white"
              aria-label="Instagram"
            >
              <Icon icon="basil:instagram-solid" width={20} height={20} />
            </a>
            <a
              href="https://x.com/nalosoft"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white"
              aria-label="X"
            >
              <Icon icon="fa7-brands:x-twitter" width={18} height={17} />
            </a>
          </nav>
        </div>

        {/* Column 2: Quick Links */}
        <nav aria-label={t("quick_links.title")}>
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white">
            {t("quick_links.title")}
          </h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li>
              <Link to={path("/")} className="hover:text-white">
                {t("quick_links.home")}
              </Link>
            </li>
            <li>
              <Link to={path("/about")} className="hover:text-white">
                {t("quick_links.about")}
              </Link>
            </li>
            <li>
              <Link
                to={path("/services/websites")}
                className="hover:text-white"
              >
                {t("quick_links.services")}
              </Link>
            </li>
            <li>
              <Link to={path("/cases")} className="hover:text-white">
                {t("quick_links.cases")}
              </Link>
            </li>
            <li>
              <Link to={path("/contact")} className="hover:text-white">
                {t("quick_links.contact")}
              </Link>
            </li>
            <li>
              <Link
                to={path("/onboard")}
                onClick={() =>
                  trackAnalyticsEvent("book_meeting_click", {
                    location: "footer_quick_links",
                    destination: "onboard",
                  })
                }
                className="hover:text-white"
              >
                {t("quick_links.onboard")}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Column 3: Services */}
        <nav aria-label={t("services.title")}>
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white">
            {t("services.title")}
          </h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li>
              <Link
                to={path("/services/websites")}
                className="hover:text-white"
              >
                {t("services.websites")}
              </Link>
            </li>
            <li>
              <Link to={path("/services/booking")} className="hover:text-white">
                {t("services.booking")}
              </Link>
            </li>
            <li>
              <Link to={path("/services/webshop")} className="hover:text-white">
                {t("services.webshop")}
              </Link>
            </li>
            <li>
              <Link
                to={path("/services/mobile-apps")}
                className="hover:text-white"
              >
                {t("services.mobile")}
              </Link>
            </li>
            <li>
              <Link
                to={path("/services/custom-solutions")}
                className="hover:text-white"
              >
                {t("services.custom")}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Column 4: Newsletter */}
        <div>
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white">
            {t("newsletter.title")}
          </h3>
          <form
            noValidate
            onSubmit={onNewsletterSubmit}
            className="space-y-3"
          >
            <label htmlFor={newsletterEmailId} className="sr-only">
              {t("newsletter.email_label")}
            </label>
            <input
              id={newsletterEmailId}
              name="newsletter-email"
              type="email"
              required
              placeholder={t("newsletter.enter_email")}
              aria-invalid={newsletterError ? "true" : undefined}
              aria-describedby={[
                newsletterHintId,
                newsletterError ? newsletterErrorId : "",
                newsletterStatus ? newsletterStatusId : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onInput={() => {
                setNewsletterError("");
                setNewsletterStatus("");
              }}
              className="w-full rounded-md border border-white/10 bg-white px-3 py-2 text-slate-950 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <p id={newsletterHintId} className="text-xs text-slate-400">
              {t("newsletter.hint")}
            </p>
            {newsletterError && (
              <p id={newsletterErrorId} role="alert" className="text-xs text-red-200">
                {newsletterError}
              </p>
            )}
            {newsletterStatus && (
              <p id={newsletterStatusId} role="status" className="text-xs text-green-200">
                {newsletterStatus}
              </p>
            )}
            <button
              type="submit"
              className="w-full rounded-md bg-white px-4 py-2 font-semibold text-slate-950 transition hover:bg-slate-200"
            >
              {t("newsletter.subscribe")}
            </button>
          </form>
        </div>
      </div>

      <TrustBar variant="dark" className="mt-10" />

      {/* Bottom bar */}
      <div className="mt-12 border-t border-white/10 py-4">
        <p className="text-center text-xs text-slate-500">
          Copyright © {new Date().getFullYear()} Nalosoft — {t("rights")}
        </p>
      </div>
    </footer>
  );
}
